<script setup lang="ts">
import { Camera } from '@lucide/vue'
import { Button, Input, ZhFieldError, ZhFormField, ZhFormLayout, ZhSavePreview } from '@zerohuman/ui'
import { computed, nextTick, ref, watch } from 'vue'
import { api } from '@/api'
import EnterpriseAvatar from '@/components/EnterpriseAvatar.vue'
import { useFieldValidation } from '@/composables/useFieldValidation'
import { avatarDataUrl } from '@/lib/avatar'
import { reloadIntoEnterprise } from '@/lib/enterprisePicker'
import { requiredName, SLUG_TAKEN } from '@/lib/fieldRules'
import { buildEnterprisePreview } from '@/lib/formPreview'

/**
 * A further enterprise for the signed-in CEO: a name, an optional slug, and the whole portal reloads into
 * it once it exists. The picker shows this inline; the map opens it from its + button. Neither needs to
 * know what happens after submit, because the reload is the outcome — hence no `created` event.
 *
 * #1756: the shared form shell (drawn as a section, since it sits inside the picker's page), one help line on every
 * field, and a plain-words preview first: nothing is created until it is confirmed.
 */
const emit = defineEmits<{ cancel: [] }>()

const name = ref('')
const slug = ref('')
/** The picked picture as a data URL, previewed here and stored once the enterprise exists. */
const avatar = ref<string | null>(null)
const avatarInput = ref<HTMLInputElement | null>(null)
const busy = ref(false)
const error = ref<string | null>(null)
const preview = computed(() => ({ name: name.value, avatarUrl: avatar.value }))
/** Name is required (the field's own rule), checked while typing (#1757); a taken slug is shown on Slug. */
const fields = useFieldValidation(
    computed(() => ({ name: name.value, slug: slug.value })),
    { name: requiredName('enterprise'), slug: () => null },
    { idPrefix: 'enterprise-create' },
)

/** The save preview's lines while it is open; any change to the form hides it as stale. */
const savePreview = ref<string[] | null>(null)

watch([name, slug, avatar], () => {
    savePreview.value = null
})

async function openPreview(): Promise<void> {
    // The kit Input hands what was typed up on the next tick: wait for it, so the preview names what was typed.
    await nextTick()
    // Validate first (CTO decision 19 on #1756): an invalid Name shows its error, takes focus and opens no preview.
    if (fields.check() !== null) return
    savePreview.value = buildEnterprisePreview({ name: name.value })
}

async function confirmPreview(): Promise<void> {
    savePreview.value = null
    await create()
}

async function onAvatarPicked(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ''
    if (!file) return
    try {
        avatar.value = await avatarDataUrl(file)
    } catch (caught) {
        error.value = `Could not use that picture${caught instanceof Error ? ` (${caught.message})` : ''}.`
    }
}

async function create(): Promise<void> {
    if (busy.value) return
    busy.value = true
    error.value = null
    // The kit Input hands what was typed up on the next tick, so a submit in the same tick as the last
    // keystroke (an autofill that submits straight away) waits for it rather than sending the value before.
    await nextTick()
    if (fields.check() !== null) {
        busy.value = false
        return
    }
    try {
        await api.createEnterprise({ name: name.value, slug: slug.value || undefined })
        // The session is signed into the new enterprise now, so its picture goes on `enterprise/avatar`. A
        // picture that fails to store is not worth losing the enterprise over: the reload still happens.
        if (avatar.value) {
            await api.setEnterpriseAvatar(avatar.value).catch(() => undefined)
        }
        reloadIntoEnterprise()
    } catch (caught) {
        const code = caught instanceof Error ? caught.message : ''
        if (code === 'slug_taken') fields.refuse('slug', SLUG_TAKEN)
        error.value =
            code === 'slug_taken'
                ? 'That slug is taken. Choose another.'
                : `Could not create the enterprise${code ? ` (${code})` : ''}.`
        busy.value = false
    }
}
</script>

<template>
	<ZhFormLayout
		title="Create enterprise"
		purpose="Creates a separate company with its own teams, memory, secrets and spend, with you as its CEO."
		heading="section"
		data-testid="enterprise-create-form"
		@submit.prevent="openPreview"
	>
		<template #status>
			<p v-if="error" class="text-destructive text-sm" role="alert">{{ error }}</p>
		</template>
		<ZhFormField label="Picture" help="Optional; its initials stand in until you pick one." example="your logo" group>
			<div class="flex items-center gap-4">
				<div class="relative shrink-0">
					<EnterpriseAvatar :enterprise="preview" class="size-20" fallback-class="text-2xl" />
					<Button
						type="button"
						size="icon-sm"
						variant="outline"
						class="absolute -right-2 -bottom-2"
						aria-label="Upload picture"
						data-testid="pick-avatar"
						:disabled="busy"
						@click="avatarInput?.click()"
					>
						<Camera />
					</Button>
					<input
						ref="avatarInput"
						type="file"
						accept="image/png,image/jpeg,image/webp,image/gif"
						class="hidden"
						data-testid="avatar-file"
						@change="onAvatarPicked"
					/>
				</div>
			</div>
		</ZhFormField>
		<!-- #1757's checks (CTO decisions 18 and 20 on #1756): a fixed id, described by the error, then the help line. -->
		<ZhFormField :id="fields.fieldId('name')" :error-id="fields.message('name').id" label="Name" help="What the company is called." example="Acme Widgets">
			<template #default="{ controlId, describedBy }">
				<Input v-model="name" required data-testid="enterprise-name" v-bind="fields.bind('name')" :id="controlId" :aria-describedby="describedBy" />
			</template>
			<template #error>
				<ZhFieldError v-bind="fields.message('name')" />
			</template>
		</ZhFormField>
		<ZhFormField :id="fields.fieldId('slug')" :error-id="fields.message('slug').id" label="Slug" help="The short name in its web address, made from the name if empty." example="acme">
			<template #default="{ controlId, describedBy }">
				<Input
					v-model="slug"
					data-testid="enterprise-slug"
					placeholder="from the name if empty"
					v-bind="fields.bind('slug')"
					:id="controlId"
					:aria-describedby="describedBy"
				/>
			</template>
			<template #error>
				<ZhFieldError v-bind="fields.message('slug')" />
			</template>
		</ZhFormField>
		<template #cancel>
			<Button type="button" size="sm" variant="outline" :disabled="busy" @click="emit('cancel')">Cancel</Button>
		</template>
		<template #primary>
			<Button type="submit" size="sm" :disabled="busy">Create and open</Button>
		</template>
		<template #preview>
			<ZhSavePreview v-if="savePreview" :lines="savePreview" confirm-label="Create and open" :pending="busy" @confirm="confirmPreview" @edit="savePreview = null" />
		</template>
	</ZhFormLayout>
</template>
